import React from "react";
import styled from "styled-components";
import { Navigation, Pagination } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { BorderRadiusTheme, ColorTheme } from "../../components/globaStyledComponents/globalStyledComponentsStyling";

interface ImageGalleryProps {
    images: string[];
    altPrefix?: string;
    fluid?: boolean;
    navigationEnabled?: boolean;
    paginationEnabled?: boolean;
    loopEnabled?: boolean;
    initialIndex?: number;
    onSlideChange?: (swiper: any) => void;
    onSlideClick?: (index: number) => void;
    maxHeight?: string;
    borderRadius?: string;
    aspectRatio?: string;
}

const GalleryWrapper = styled(Swiper)<{ $fluid?: boolean; $maxHeight?: string; $borderRadius?: string; $aspectRatio?: string }>`
    width: 100%;
    max-width: ${props => (props.$fluid ? '100%' : '520px')};
    height: ${props => (props.$fluid ? '100%' : '300px')};
    max-height: ${props => props.$maxHeight || 'none'};
    aspect-ratio: ${props => props.$aspectRatio || 'none'};
    border-radius: ${props => props.$borderRadius || (props.$fluid ? 'inherit' : BorderRadiusTheme.radius_24.borderRadius)};
    overflow: hidden;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
    -webkit-tap-highlight-color: transparent;

    @media (min-width: 768px) {
        width: ${props => (props.$fluid ? '100%' : '520px')};
        height: ${props => (props.$fluid ? '100%' : '520px')};
        max-height: ${props => props.$maxHeight || 'none'};
        aspect-ratio: ${props => props.$aspectRatio || 'none'};
    }

    .swiper-slide {
        width: 100%;
        height: 100%;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
    }

    .swiper-button-next:after,
    .swiper-button-prev:after {
        content: '';
        width: 16px;
        height: 16px;
        background-image: url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='none'%20stroke='currentColor'%20stroke-width='2'%20stroke-linecap='round'%20stroke-linejoin='round'%3E%3Cpath%20d='m9%2018%206-6-6-6'/%3E%3C/svg%3E");
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    .swiper-button-prev:after {
        transform: translate(-50%, -50%) rotate(180deg);
    }

    .swiper-button-prev,
    .swiper-button-next {
        height: 32px;
        width: 32px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.75);
        color: #fff;
        border: 1px solid rgba(255, 255, 255, 0.55);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        transition: all 125ms ease;
        opacity: 0;
    }

    .swiper-button-prev:hover,
    .swiper-button-next:hover {
        transform: scale(1.05);
    }

    .swiper-button-disabled {
        display: none;
    }

    .swiper-button-disabled:hover {
        transform: scale(1);
    }

    &:hover {
        .swiper-button-prev,
        .swiper-button-next {
            opacity: 1;
            border: 1px solid #e5e5e5;
        }
    }

    .swiper-pagination-bullet {
        background: white;
        opacity: 0.7;
    }

    .swiper-pagination-bullet-active {
        opacity: 1;
    }
`;

const GalleryImage = styled.img<{ $fluid?: boolean; $maxHeight?: string; $borderRadius?: string; $aspectRatio?: string }>`
    width: 100%;
    max-width: ${props => (props.$fluid ? '100%' : '520px')};
    height: ${props => (props.$fluid ? '100%' : '300px')};
    max-height: ${props => props.$maxHeight || 'none'};
    aspect-ratio: ${props => props.$aspectRatio || 'none'};
    object-fit: cover;
    border-radius: ${props => props.$borderRadius || (props.$fluid ? 'inherit' : BorderRadiusTheme.radius_24.borderRadius)};
    box-shadow: ${props => (props.$fluid ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.1)')};

    @media (min-width: 768px) {
        width: ${props => (props.$fluid ? '100%' : '520px')};
        height: ${props => (props.$fluid ? '100%' : '520px')};
        max-height: ${props => props.$maxHeight || 'none'};
        aspect-ratio: ${props => props.$aspectRatio || 'none'};
    }
`;

export default function ImageGallery({ images, altPrefix = "Image", fluid = false, navigationEnabled = true, paginationEnabled = true, loopEnabled = true, initialIndex = 0, onSlideChange, onSlideClick, maxHeight, borderRadius, aspectRatio }: ImageGalleryProps) {
    if (!images || images.length === 0) return null;

    return (
        <GalleryWrapper
            $fluid={fluid}
            $maxHeight={maxHeight}
            $borderRadius={borderRadius}
            $aspectRatio={aspectRatio}
            modules={[Navigation, Pagination]}
            navigation={navigationEnabled}
            centeredSlides={true}
            pagination={paginationEnabled ? {
                clickable: true,
                dynamicBullets: true,
                dynamicMainBullets: 3,
            } : false}
            loop={loopEnabled}
            initialSlide={initialIndex}
            spaceBetween={0}
            slidesPerView={1}
            speed={150}
            resistanceRatio={0}
            shortSwipes={true}
            threshold={1}
            onSlideChange={onSlideChange}
            style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'translateZ(0)',
                WebkitTransform: 'translateZ(0)',
                willChange: 'transform',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
            }}
        >
            {images.map((src, index) => (
                <SwiperSlide key={index} onClick={() => onSlideClick && onSlideClick(index)}>
                    <GalleryImage $fluid={fluid} $maxHeight={maxHeight} $borderRadius={borderRadius} $aspectRatio={aspectRatio} src={src} alt={`${altPrefix} - Image ${index + 1}`} loading="lazy" />
                </SwiperSlide>
            ))}
        </GalleryWrapper>
    );
}


